import { AuthService } from "@/online/AuthService";
import type { Platform } from "@/platform/Platform";
import { getSupabaseClient } from "@/online/supabaseClient";
import { callFunction } from "@/online/functionsClient";
import type { ScoreChallengeStatus, ScoreChallengeView } from "./scoreChallenge";
import { DAILY_SCORING_RULES_VERSION } from "./types";

export type ScoreChallengeServiceState = "ok" | "offline" | "error";

export interface CreateScoreChallengeResult {
  state: ScoreChallengeServiceState | "not_ranked";
  publicId?: string;
  targetDailyPoints?: number;
  expiresAtMs?: number;
  /** The caller's OWN display-name snapshot as actually stored on the (possibly pre-existing) challenge row - always the RPC's returned `creator_display_name_snapshot`, never re-derived client-side, so a reshare of an unchanged best shows the exact same name the friend's landing page will ("Your public name X ... will be visible" - see the share modal's disclosure copy). */
  creatorDisplayName?: string;
  message?: string;
}

export interface GetScoreChallengeResult {
  state: ScoreChallengeServiceState;
  view?: ScoreChallengeView;
  message?: string;
}

export interface AssociateScoreChallengeResult {
  state: ScoreChallengeServiceState;
}

interface CreateScoreChallengeFunctionResponse {
  publicId: string;
  targetDailyPoints: number;
  expiresAtMs: number;
  creatorDisplayName: string;
}

interface ScoreChallengeRpcRow {
  status: ScoreChallengeStatus;
  challenge_id: string | null;
  target_daily_points: number | null;
  creator_display_name: string | null;
  analytics_id: string | null;
  is_creator: boolean;
  expires_at: string | null;
}

/**
 * The one place "Beat My Score" talks to the network - mirrors
 * `DailyChallengeClient`'s own read/write split exactly: the one write
 * that must independently re-verify the caller's ranked result
 * (`create-score-challenge`) goes through a Netlify Function; the public
 * read (`get_score_challenge`) is a plain SECURITY DEFINER RPC called
 * directly, since nothing it returns is more sensitive than a leaderboard
 * row. Every public method resolves, never throws.
 */
class ScoreChallengeClientImpl {
  /**
   * Every public method on this class must resolve, never throw or leave a
   * caller awaiting forever - this is the one place that guarantee could
   * previously break: `AuthService.ensureSignedInForDaily()`/
   * `client.auth.getSession()` were called with no try/catch of their own,
   * so an unexpected rejection here (a corrupted stored session, a
   * transient Supabase Auth failure) became an UNHANDLED PROMISE REJECTION
   * that propagated straight through `getChallenge()`/`createOrGetChallenge()`/
   * `associateSource()` - none of which wrap this call either - leaving
   * `ScoreChallengeScene.loadChallenge()`'s `await` never resolving and the
   * scene stuck on "LOADING CHALLENGE…" forever, with no visible error.
   * Wrapped here once so every caller is protected by construction.
   */
  private async ensureSignedIn(): Promise<{ token: string } | null> {
    try {
      const client = getSupabaseClient();
      if (!client) return null;
      if (!(await AuthService.ensureSignedInForDaily())) return null;
      const { data } = await client.auth.getSession();
      const token = data.session?.access_token;
      return token ? { token } : null;
    } catch {
      return null;
    }
  }

  private classifyError(): "offline" | "error" {
    return typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "error";
  }

  /**
   * Creates (or idempotently re-returns) a challenge for the CALLER's own
   * current verified ranked Daily V2 best - never accepts, and has no way
   * to accept, a score/date/name from this client; the Netlify Function
   * independently re-derives all of it server-side from
   * `daily_participation_bests` (the caller's own current-version
   * competitive best - see `DAILY_SCORING_RULES_VERSION`'s doc). Used
   * both for the original "CHALLENGE A FRIEND" on a fresh result and for
   * "CHALLENGE A FRIEND" after a challenge comparison (a re-share) - the
   * two are indistinguishable at this layer by design, since both are
   * simply "give me a link for my current best."
   */
  async createOrGetChallenge(platform: Platform): Promise<CreateScoreChallengeResult> {
    const auth = await this.ensureSignedIn();
    if (!auth) return { state: this.classifyError() };

    // PR34: the target is the caller's best on THIS platform (Daily bests
    // are per Mobile/Desktop); the resulting link itself is platform-neutral.
    const result = await callFunction<CreateScoreChallengeFunctionResponse>("score-challenge-create", auth.token, { platform });
    if (!result.ok) {
      if (result.status === 422 && result.message === "not_ranked") return { state: "not_ranked", message: result.message };
      return { state: result.status >= 400 && result.status < 500 ? "error" : this.classifyError(), message: result.message };
    }
    return {
      state: "ok",
      publicId: result.data.publicId,
      targetDailyPoints: result.data.targetDailyPoints,
      expiresAtMs: result.data.expiresAtMs,
      creatorDisplayName: result.data.creatorDisplayName,
    };
  }

  /**
   * Public lookup by `publicId` - always signs in first (an anonymous
   * session is enough) so `is_creator` resolves correctly and so the
   * caller can immediately follow up with the normal Daily status/rank
   * reads, which all require a session anyway.
   */
  async getChallenge(publicId: string): Promise<GetScoreChallengeResult> {
    const auth = await this.ensureSignedIn();
    if (!auth) return { state: this.classifyError() };
    const client = getSupabaseClient();
    if (!client) return { state: this.classifyError() };

    try {
      // PR26 follow-up: a challenge pinned to a DIFFERENT scoring-rules
      // version than THIS client's current one comes back status:'expired'
      // instead of 'active' - see get_score_challenge()'s own doc.
      const { data, error } = await client.rpc("get_score_challenge", { p_public_id: publicId, p_rules_version: DAILY_SCORING_RULES_VERSION });
      if (error) return { state: this.classifyError() };
      const row = ((data ?? []) as ScoreChallengeRpcRow[])[0];
      if (!row) return { state: "ok", view: { status: "invalid", challengeId: null, targetDailyPoints: null, creatorDisplayName: null, analyticsId: null, isCreator: false, expiresAtMs: null } };
      return {
        state: "ok",
        view: {
          status: row.status,
          challengeId: row.challenge_id,
          targetDailyPoints: row.target_daily_points,
          creatorDisplayName: row.creator_display_name,
          analyticsId: row.analytics_id,
          isCreator: row.is_creator,
          expiresAtMs: row.expires_at ? Date.parse(row.expires_at) : null,
        },
      };
    } catch {
      return { state: this.classifyError() };
    }
  }

  /**
   * Best-effort, fire-and-forget-shaped verified attribution: tells the
   * server "the currently signed-in player landed on this active
   * challenge link." Silently a no-op server-side (never surfaced
   * differently here) for a self-referral, an expired/unknown token, or a
   * token from a different Daily date - see
   * `associate_daily_challenge_source()`'s doc.
   */
  async associateSource(publicId: string): Promise<AssociateScoreChallengeResult> {
    const auth = await this.ensureSignedIn();
    if (!auth) return { state: this.classifyError() };
    const result = await callFunction<{ ok: boolean }>("score-challenge-associate", auth.token, { publicId });
    if (!result.ok) return { state: result.status >= 400 && result.status < 500 ? "error" : this.classifyError() };
    return { state: "ok" };
  }
}

export const ScoreChallengeClient = new ScoreChallengeClientImpl();
